import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  BookingStatus,
  Chat,
  ChatMessage,
  ChatMessageType,
  ChatState,
  CurrentChat,
} from './model';
import { CHAT_ACTIONS, CHAT_LOADERS, ChatTabValue } from './utils';

const initialState: ChatState = {
  newMessagesCount: 0,
  chatFilter: 'all',
  chats: [],
  loaders: {},
};

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    changeChatFilter: (state, { payload }: PayloadAction<ChatTabValue>) => {
      state.chatFilter = payload;
    },
    setLoader: (
      state,
      {
        payload: { key, value },
      }: PayloadAction<{ key: CHAT_LOADERS; value: boolean }>,
    ) => {
      state.loaders[key] = value;
    },
    setChats: (state, { payload }: PayloadAction<Chat[]>) => {
      state.chats = payload;
    },
    setCurrentChat: (state, { payload }: PayloadAction<CurrentChat>) => {
      state.currentChat = payload;
    },
    addMessageToChat: (state, { payload }: PayloadAction<ChatMessage>) => {
      state.currentChat.messages.push(payload);
    },
    updateBookingStatusByMessageId: (
      state,
      {
        payload,
      }: PayloadAction<{
        messageId: string;
        bookingId: string;
        newStatus: BookingStatus;
      }>,
    ) => {
      const { messageId, bookingId, newStatus } = payload;
      if (state.currentChat)
        state.currentChat.messages.forEach((message) => {
          if (message._id === messageId && message.booking._id === bookingId) {
            message.booking.status = newStatus;
          }
        });
    },
    updateBookingStatusByBookingId: (
      state,
      {
        payload,
      }: PayloadAction<{
        bookingId: string;
        newStatus: BookingStatus;
      }>,
    ) => {
      const { bookingId, newStatus } = payload;
      if (state.currentChat)
        state.currentChat.messages.forEach((message) => {
          if (message.booking?._id === bookingId) {
            message.booking.status = newStatus;
          }
        });
    },
    removeBookingFromChat: (state, { payload }: PayloadAction<string>) => {
      if (state.currentChat)
        state.currentChat.messages = state.currentChat.messages.filter(
          (message) => message.booking?._id !== payload,
        );
    },
    reset: () => initialState,
  },
});

const extraActions = {
  fetchMessages: createAction(CHAT_ACTIONS.FETCH_MESSAGES),
  getChatById: createAction<{ chatId: string }>(CHAT_ACTIONS.GET_CHAT_BY_ID),
  sendMessage: createAction<{
    message: string;
    bookingId?: string;
    type?: ChatMessageType;
    chatId?: string;
  }>(CHAT_ACTIONS.SEND_MESSAGE),
};

export const {
  reset,
  setChats,
  changeChatFilter,
  setLoader,
  fetchMessages,
  setCurrentChat,
  getChatById,
  sendMessage,
  addMessageToChat,
  updateBookingStatusByMessageId,
  updateBookingStatusByBookingId,
  removeBookingFromChat,
} = { ...chatSlice.actions, ...extraActions };

export const chatReducer = chatSlice.reducer;
